import { useMemo } from "react";
import { useContasPagar } from "./useContasPagar";
import { useContasReceber } from "./useContasReceber";
import { calcularStatus } from "../types";

export const useFluxoCaixa = (startDate?: string, endDate?: string) => {
  const { contas: contasPagar, isLoading: loadingPagar } = useContasPagar({ tamanhoPagina: 100 });
  const { contas: contasReceber, isLoading: loadingReceber } = useContasReceber({ tamanhoPagina: 100 });

  const isLoading = loadingPagar || loadingReceber;

  // Filter contas by date range if provided
  const filteredContasPagar = useMemo(() => {
    if (!startDate || !endDate) return contasPagar;
    return contasPagar.filter((conta) => {
      const dataRef = conta.dtPagamento ?? conta.dtVencimento;
      return dataRef >= startDate && dataRef <= endDate;
    });
  }, [contasPagar, startDate, endDate]);

  const filteredContasReceber = useMemo(() => {
    if (!startDate || !endDate) return contasReceber;
    return contasReceber.filter((conta) => {
      const dataRef = conta.dtPagamento ?? conta.dtVencimento;
      return dataRef >= startDate && dataRef <= endDate;
    });
  }, [contasReceber, startDate, endDate]);

  // Calculate totals
  const totalEntradas = useMemo(() => {
    return filteredContasReceber
      .filter((c) => c.isPago)
      .reduce((sum, c) => sum + c.valorPago, 0);
  }, [filteredContasReceber]);

  const totalSaidas = useMemo(() => {
    return filteredContasPagar
      .filter((c) => c.isPago)
      .reduce((sum, c) => sum + (c.valorTotal - c.valorSaldo), 0);
  }, [filteredContasPagar]);

  const totalAPagar = useMemo(() => {
    return filteredContasPagar
      .filter((c) => !c.isPago)
      .reduce((sum, c) => sum + c.valorSaldo, 0);
  }, [filteredContasPagar]);

  const totalAReceber = useMemo(() => {
    return filteredContasReceber
      .filter((c) => !c.isPago)
      .reduce((sum, c) => sum + c.valorSaldo, 0);
  }, [filteredContasReceber]);

  const saldoAtual = totalEntradas - totalSaidas;
  const saldoPrevisto = saldoAtual + totalAReceber - totalAPagar;

  // Group by date for chart
  const fluxoData = useMemo(() => {
    const dateMap = new Map<string, { date: string; entradas: number; saidas: number }>();

    filteredContasReceber
      .filter((c) => c.isPago && c.dtPagamento)
      .forEach((conta) => {
        const date = new Date(conta.dtPagamento!).toLocaleDateString("pt-BR", {
          day: "2-digit",
          month: "2-digit",
        });
        const existing = dateMap.get(date) || { date, entradas: 0, saidas: 0 };
        existing.entradas += conta.valorPago;
        dateMap.set(date, existing);
      });

    filteredContasPagar
      .filter((c) => c.isPago && c.dtPagamento)
      .forEach((conta) => {
        const date = new Date(conta.dtPagamento!).toLocaleDateString("pt-BR", {
          day: "2-digit",
          month: "2-digit",
        });
        const existing = dateMap.get(date) || { date, entradas: 0, saidas: 0 };
        existing.saidas += conta.valorTotal - conta.valorSaldo;
        dateMap.set(date, existing);
      });

    return Array.from(dateMap.values()).sort((a, b) => {
      const [dayA, monthA] = a.date.split("/").map(Number);
      const [dayB, monthB] = b.date.split("/").map(Number);
      return monthA !== monthB ? monthA - monthB : dayA - dayB;
    });
  }, [filteredContasReceber, filteredContasPagar]);

  // Group expenses by description (no category field in API)
  const despesasData = useMemo(() => {
    const descMap = new Map<string, number>();

    filteredContasPagar
      .filter((c) => c.isPago)
      .forEach((conta) => {
        const status = calcularStatus(conta.isPago, conta.dtVencimento);
        if (status === "pago") {
          const chave = conta.numDocumento ?? `Conta #${conta.id}`;
          const current = descMap.get(chave) || 0;
          descMap.set(chave, current + (conta.valorTotal - conta.valorSaldo));
        }
      });

    return Array.from(descMap.entries())
      .map(([categoria, valor]) => ({ categoria, valor }))
      .sort((a, b) => b.valor - a.valor)
      .slice(0, 10);
  }, [filteredContasPagar]);

  return {
    isLoading,
    totalEntradas,
    totalSaidas,
    totalAPagar,
    totalAReceber,
    saldoAtual,
    saldoPrevisto,
    fluxoData,
    despesasData,
  };
};
